import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  SituationTaxonomyMember,
  SituationTaxonomyMembersResponse,
  CreateSituationTaxonomyMemberPayload,
  ResponseTaxonomyMember,
  ResponseTaxonomyMembersResponse,
  CreateResponseTaxonomyMemberPayload,
  CdeSituationTaxonomyMember,
} from '../../shared/models/taxonomy-member.models';
import { PageParams } from '../../shared/models/pagination.models';

@Injectable({ providedIn: 'root' })
export class TaxonomyMemberService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  // ── Situation Taxonomy Members ────────────────────────────────────────────

  getSituationTaxonomyMembers(
    taxonomyId: string,
    params: PageParams = { pageSize: 10, pageIndex: 0 },
  ) {
    let httpParams = new HttpParams()
      .set('SituationTaxonomyId', taxonomyId)
      .set('pageIndex', params.pageIndex ?? 0);
    if (params.pageSize != null) httpParams = httpParams.set('pageSize', params.pageSize);
    return this.http.get<SituationTaxonomyMembersResponse>(
      `${this.apiUrl}/api/situation-taxonomy-members`,
      { params: httpParams },
    );
  }

  /**
   * Data elements linked to a situation category (JCCA-159). Read from their own endpoint:
   * the member GET by id doesn't load them.
   */
  getCdeSituationTaxonomyMembers(situationTaxonomyMemberId: string) {
    return this.http.get<{ cdeSituationTaxonomyMembers: CdeSituationTaxonomyMember[] }>(
      `${this.apiUrl}/api/cde-situation-taxonomy-members`,
      { params: new HttpParams().set('situationTaxonomyMemberId', situationTaxonomyMemberId) },
    );
  }

  getSituationTaxonomyMemberById(id: string) {
    return this.http.get<SituationTaxonomyMember>(
      `${this.apiUrl}/api/situation-taxonomy-members/${id}`,
    );
  }

  createSituationTaxonomyMember(payload: CreateSituationTaxonomyMemberPayload) {
    return this.http.post<SituationTaxonomyMember>(
      `${this.apiUrl}/api/situation-taxonomy-members`,
      payload,
    );
  }

  updateSituationTaxonomyMember(payload: SituationTaxonomyMember) {
    return this.http.put<SituationTaxonomyMember>(
      `${this.apiUrl}/api/situation-taxonomy-members`,
      payload,
    );
  }

  deleteSituationTaxonomyMember(id: string) {
    return this.http.delete<void>(`${this.apiUrl}/api/situation-taxonomy-members/${id}`);
  }

  // ── Response Taxonomy Members ─────────────────────────────────────────────

  getResponseTaxonomyMembers(
    taxonomyId: string,
    params: PageParams = { pageSize: 10, pageIndex: 0 },
  ) {
    let httpParams = new HttpParams()
      .set('ResponseTaxonomyId', taxonomyId)
      .set('pageIndex', params.pageIndex ?? 0);
    if (params.pageSize != null) httpParams = httpParams.set('pageSize', params.pageSize);
    return this.http.get<ResponseTaxonomyMembersResponse>(
      `${this.apiUrl}/api/response-taxonomy-members`,
      { params: httpParams },
    );
  }

  getResponseTaxonomyMemberById(id: string) {
    return this.http.get<ResponseTaxonomyMember>(
      `${this.apiUrl}/api/response-taxonomy-members/${id}`,
    );
  }

  createResponseTaxonomyMember(payload: CreateResponseTaxonomyMemberPayload) {
    return this.http.post<ResponseTaxonomyMember>(
      `${this.apiUrl}/api/response-taxonomy-members`,
      payload,
    );
  }

  updateResponseTaxonomyMember(payload: ResponseTaxonomyMember) {
    return this.http.put<ResponseTaxonomyMember>(
      `${this.apiUrl}/api/response-taxonomy-members`,
      payload,
    );
  }

  deleteResponseTaxonomyMember(id: string) {
    return this.http.delete<void>(`${this.apiUrl}/api/response-taxonomy-members/${id}`);
  }
}
